import { PositionInTheCompanyChoice, SocietyUserVar } from "@dune/types";
import {
	Button,
	ControlledDropdown,
	ControlledInput,
	ControlledPhoneInput,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	RootDialogProps,
} from "@dune/ui";
import { brokerUserCreationSubRolesLabels, isBrokerSubRole } from "@dune/vars";
import React from "react";
import { toast } from "sonner";
import { useUpdateUser } from "../../../../../../api/mutations/user.mutations";
import { useGetBrokerEntities } from "../../../../../../api/queries/societies.queries";
import { AddUserFormData, useAddUserForm } from "./useAddUserForm";

export const EditUserDialog = (
	props: RootDialogProps & { user: SocietyUserVar },
) => {
	const { user } = props;
	const { data: entities = [] } = useGetBrokerEntities(user.societyId || 0);
	const { mutate, isPending } = useUpdateUser(user.societyId || 0);
	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useAddUserForm({
		initialValues: {
			entity: user.entity || "",
			lastname: user.lastname,
			firstname: user.firstname,
			email: user.email || "",
			mobilePhone: user.mobilePhone || "",
			landlinePhone: user.landlinePhone || "",
			positionInTheCompany: user.positionInTheCompany || undefined,
			subrole: isBrokerSubRole(user.subrole) ? user.subrole : undefined,
		},
	});

	const entityOptions = React.useMemo(() => {
		if (!entities || entities.length === 0) return { "": "Aucune entité" };
		return entities.reduce(
			(acc, entity) => {
				acc[entity] = entity;
				return acc;
			},
			{ "": "Aucune entité" } as Record<string, string>,
		);
	}, [entities]);

	const hasEntities = entities && entities.length > 0;

	if (!user.societyId) {
		toast.error("Erreur : ID de société manquant");
		props.onOpenChange?.(false);
		return null;
	}

	const onSubmit = (data: AddUserFormData) => {
		mutate(
			{
				user: {
					id: user.id,
					email: data.email,
					firstname: data.firstname,
					lastname: data.lastname,
					subrole: data.subrole,
					positionInTheCompany: data.positionInTheCompany,
					mobilePhone: data.mobilePhone,
					landlinePhone: data.landlinePhone,
					entity: data.entity,
				},
			},
			{
				onSuccess: () => {
					props.onOpenChange?.(false);
				},
			},
		);
	};

	return (
		<Dialog {...props}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Modifier un utilisateur</DialogTitle>
				</DialogHeader>

				<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
					<ControlledDropdown
						control={control}
						name="entity"
						label="Entité"
						options={entityOptions}
						error={errors.entity?.message}
						disabled={!hasEntities}
					/>
					{!hasEntities && (
						<span className="text-gray-500 text-sm -mt-2">
							Aucune entité n'est configurée pour ce broker
						</span>
					)}

					<div className="grid grid-cols-2 gap-4">
						<ControlledInput
							control={control}
							name="lastname"
							label="Nom"
							error={errors.lastname?.message}
						/>
						<ControlledInput
							control={control}
							name="firstname"
							label="Prénom"
							error={errors.firstname?.message}
						/>
					</div>

					<ControlledInput
						control={control}
						name="email"
						label="Email"
						type="email"
						error={errors.email?.message}
					/>

					<div className="grid grid-cols-2 gap-4">
						<ControlledPhoneInput
							control={control}
							name="landlinePhone"
							label="Téléphone fixe"
							defaultCountry="FR"
							error={errors.landlinePhone?.message}
						/>
						<ControlledPhoneInput
							control={control}
							name="mobilePhone"
							label="Mobile"
							defaultCountry="FR"
							error={errors.mobilePhone?.message}
						/>
					</div>

					<ControlledDropdown
						control={control}
						name="positionInTheCompany"
						label="Fonction"
						options={PositionInTheCompanyChoice}
						error={errors.positionInTheCompany?.message}
					/>

					<ControlledDropdown
						control={control}
						name="subrole"
						label="Profil"
						options={brokerUserCreationSubRolesLabels}
						error={errors.subrole?.message}
					/>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => props.onOpenChange?.(false)}
						>
							Annuler
						</Button>
						<Button type="submit" isLoading={isPending}>
							Modifier
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};
